/**
 * Benefits Tracker Component
 * Displays program benefits and milestones
 */
import React, { useState, useEffect } from 'react'
import { Award, Target, Plus, Filter, X } from 'lucide-react'
import { programBenefitsService, ProgramBenefit, BenefitMilestone } from '../../services/programBenefits'
import { toast } from 'sonner'
import { getErrorMessage, formatCurrency } from '../../utils/apiTransformers'
import type { ApiErrorLike } from '../../utils/apiError'

interface BenefitsTrackerProps {
  programId: number
}

const EMPTY_BENEFIT = {
  benefit_name: '',
  category: 'efficiency',
  target_value: 0,
  realized_value: 0,
  projected_value: 0,
  unit: '',
  status: 'planned',
  measurement_method: '',
}

export default function BenefitsTracker({ programId }: BenefitsTrackerProps) {
  const [benefits, setBenefits] = useState<ProgramBenefit[]>([])
  const [milestones, setMilestones] = useState<BenefitMilestone[]>([])
  const [loading, setLoading] = useState(true)
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState<typeof EMPTY_BENEFIT>(EMPTY_BENEFIT)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadBenefits()
  }, [programId, filterCategory, filterStatus])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.benefit_name.trim()) { toast.error('Benefit name is required'); return }
    setSaving(true)
    try {
      await programBenefitsService.createBenefit(programId, formData)
      toast.success('Benefit created')
      setShowModal(false)
      setFormData(EMPTY_BENEFIT)
      loadBenefits()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to create benefit')
    } finally {
      setSaving(false)
    }
  }

  const loadBenefits = async () => {
    setLoading(true)
    try {
      const [benefitsData, milestonesData] = await Promise.all([
        programBenefitsService.listBenefits(programId),
        programBenefitsService.listMilestones(programId)
      ])
      setBenefits(benefitsData)
      setMilestones(milestonesData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading benefits:', error)
      toast.error(getErrorMessage(error) || 'Failed to load benefits')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'realized':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'at_risk':
        return 'bg-red-100 text-red-700 border-red-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getAchievementRate = (benefit: ProgramBenefit) => {
    if (benefit.target_value === 0) return 0
    return (benefit.realized_value / benefit.target_value) * 100
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#f0f4f8] rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-[#f0f4f8] rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const filteredBenefits = benefits.filter(b => {
    if (filterCategory !== 'all' && b.category !== filterCategory) return false
    if (filterStatus !== 'all' && b.status !== filterStatus) return false
    return true
  })

  const totalTarget = benefits.reduce((sum, b) => sum + b.target_value, 0)
  const totalRealized = benefits.reduce((sum, b) => sum + b.realized_value, 0)
  const overallAchievement = totalTarget > 0 ? (totalRealized / totalTarget) * 100 : 0

  return (
    <>
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Award className="w-6 h-6 text-[#486581]" />
            <h3 className="text-xl font-semibold text-[#102a43]">Benefits Realization</h3>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            New Benefit
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-[#f0f4f8] rounded-lg">
            <div className="text-sm text-[#829ab1] mb-1">Total Benefits</div>
            <div className="text-2xl font-bold text-[#102a43]">{benefits.length}</div>
          </div>
          <div className="p-4 bg-[#f0f4f8] rounded-lg">
            <div className="text-sm text-[#829ab1] mb-1">Target Value</div>
            <div className="text-2xl font-bold text-[#102a43]">{formatCurrency(totalTarget)}</div>
          </div>
          <div className="p-4 bg-[#f0f4f8] rounded-lg">
            <div className="text-sm text-[#829ab1] mb-1">Realized Value</div>
            <div className="text-2xl font-bold text-[#102a43]">{formatCurrency(totalRealized)}</div>
          </div>
          <div className="p-4 bg-[#f0f4f8] rounded-lg">
            <div className="text-sm text-[#829ab1] mb-1">Achievement Rate</div>
            <div className="text-2xl font-bold text-[#102a43]">{overallAchievement.toFixed(1)}%</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#829ab1]" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-1.5 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white"
            >
              <option value="all">All Categories</option>
              <option value="revenue_growth">Revenue Growth</option>
              <option value="cost_reduction">Cost Reduction</option>
              <option value="efficiency">Efficiency</option>
              <option value="risk_mitigation">Risk Mitigation</option>
            </select>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="realized">Realized</option>
            <option value="at_risk">At Risk</option>
          </select>
        </div>
      </div>

      {/* Benefits List */}
      {filteredBenefits.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
          <div className="text-center py-12">
            <Award className="w-12 h-12 text-[#829ab1] mx-auto mb-4" />
            <p className="text-[#829ab1]">No benefits found</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBenefits.map((benefit) => {
            const achievementRate = getAchievementRate(benefit)
            const benefitMilestones = milestones.filter(m => m.benefit_id === benefit.id)
            
            return (
              <div
                key={benefit.id}
                className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Target className="w-5 h-5 text-[#486581]" />
                      <h4 className="font-medium text-[#102a43]">{benefit.benefit_name}</h4>
                      <span className="px-2 py-0.5 text-xs rounded border bg-blue-100 text-blue-700 border-blue-200 capitalize">
                        {benefit.category.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(benefit.status)}`}>
                        {benefit.status.replace('_', ' ')}
                      </span>
                    </div>
                    {benefit.measurement_method && (
                      <p className="text-sm text-[#829ab1] mb-3">{benefit.measurement_method}</p>
                    )}
                  </div>
                </div>

                {/* Benefit Metrics */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="p-3 bg-[#f0f4f8] rounded-lg">
                    <div className="text-xs text-[#829ab1] mb-1">Target</div>
                    <div className="text-lg font-semibold text-[#102a43]">
                      {formatCurrency(benefit.target_value)} {benefit.unit}
                    </div>
                  </div>
                  <div className="p-3 bg-[#f0f4f8] rounded-lg">
                    <div className="text-xs text-[#829ab1] mb-1">Realized</div>
                    <div className="text-lg font-semibold text-[#102a43]">
                      {formatCurrency(benefit.realized_value)} {benefit.unit}
                    </div>
                  </div>
                  <div className="p-3 bg-[#f0f4f8] rounded-lg">
                    <div className="text-xs text-[#829ab1] mb-1">Achievement</div>
                    <div className="text-lg font-semibold text-[#102a43]">
                      {achievementRate.toFixed(1)}%
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="w-full h-3 bg-[#f0f4f8] rounded-full">
                    <div
                      className={`h-3 rounded-full ${
                        achievementRate >= 100 ? 'bg-green-500' :
                        achievementRate >= 80 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(100, achievementRate)}%` }}
                    />
                  </div>
                </div>

                {/* Milestones */}
                {benefitMilestones.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#bcccdc]">
                    <h5 className="text-sm font-semibold text-[#102a43] mb-3">Milestones</h5>
                    <div className="space-y-2">
                      {benefitMilestones.slice(0, 3).map((milestone) => (
                        <div key={milestone.id} className="flex items-center justify-between text-xs">
                          <span className="text-[#102a43]">{milestone.milestone_name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[#829ab1]">
                              {new Date(milestone.target_date).toLocaleDateString()}
                            </span>
                            <span className={`px-2 py-0.5 rounded border ${
                              milestone.status === 'complete' ? 'bg-green-100 text-green-700 border-green-200' :
                              milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                              'bg-gray-100 text-gray-700 border-gray-200'
                            }`}>
                              {milestone.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>

    {/* Create Benefit Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#102a43]">New Benefit</h2>
            <button onClick={() => { setShowModal(false); setFormData(EMPTY_BENEFIT) }} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <form onSubmit={handleCreate} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Benefit Name *</label>
              <input type="text" required value={formData.benefit_name} onChange={e => setFormData(p => ({ ...p, benefit_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900">
                  <option value="revenue_growth">Revenue Growth</option>
                  <option value="cost_reduction">Cost Reduction</option>
                  <option value="efficiency">Efficiency</option>
                  <option value="risk_mitigation">Risk Mitigation</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select value={formData.status} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900">
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="realized">Realized</option>
                  <option value="at_risk">At Risk</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Target Value</label>
                <input type="number" step="0.01" value={formData.target_value} onChange={e => setFormData(p => ({ ...p, target_value: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Projected</label>
                <input type="number" step="0.01" value={formData.projected_value} onChange={e => setFormData(p => ({ ...p, projected_value: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
                <input type="text" value={formData.unit} onChange={e => setFormData(p => ({ ...p, unit: e.target.value }))}
                  placeholder="e.g. USD, %"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Measurement Method</label>
              <input type="text" value={formData.measurement_method} onChange={e => setFormData(p => ({ ...p, measurement_method: e.target.value }))}
                placeholder="How will this benefit be measured?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
              <button type="button" onClick={() => { setShowModal(false); setFormData(EMPTY_BENEFIT) }}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] disabled:opacity-50 flex items-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  )
}

